// Skill action hooks for the authored hero (?anims=new, owner approval pending). Turns SimEvents and sim state into
// GltfHero action-layer calls (heroGltf.ts) and draws fresh shots leaving the hand that fired them: a young projectile is
// drawn from the live hand-bone position, easing onto its sim path within ~0.1 s. Render-only: reads Sim, never writes.
import * as THREE from 'three/webgpu';
import type { Projectile, Sim, SimEvent } from '../core/sim';
import type { GltfHero } from './heroGltf';

/** seconds a fresh shot takes to ease from the hand onto its sim path, per projectile kind */
const MUZZLE_T: Record<number, number> = { 0: 0.12, 1: 0.12, 2: 0.2, 5: 0.12, 6: 0.1 };
type Side = 'L' | 'R';
const yawOf = (dx: number | undefined, dz: number | undefined): number | undefined => (dx === undefined || dz === undefined ? undefined : Math.atan2(dx, dz));

export class HeroActionHooks {
  private muz = new Map<Projectile, { age: number; side: Side; T: number }>();
  private sky = new Map<object, THREE.Vector3>(); private skyCap = 0;
  private castN = 0;
  private readonly _h = new THREE.Vector3(); private readonly _h2 = new THREE.Vector3();
  private readonly _o = { x: 0, y: 0, z: 0 };
  /** QA: shots drawn from a hand so far */
  stats = { tagged: 0 };

  constructor(private heroOf: () => GltfHero | null) {}

  private hero(): GltfHero | null {
    const h = this.heroOf();
    return h && h.ready && h.actionsReady && h.root.visible ? h : null;
  }
  /** full action layer (?anims=new) active on the visible authored hero (Rift Mortar / Zenith Orb then use their own clips) */
  get live(): boolean { return !!this.hero()?.actionsLive; }

  /** call for every SimEvent (before the view's own handling) */
  event(ev: SimEvent, sim: Sim): void {
    const h = this.hero(); if (!h) return;
    const p = sim.player, aim = Math.atan2(p.aimX, p.aimZ);
    switch (ev.t) {
      case 'shoot':
        // Spirit Bolt: alternating one-hand thrust per shot; the bolt is drawn leaving that hand
        if (ev.w === 'bolt' && h.allows('bolt_r')) { const side: Side = this.castN++ % 2 ? 'L' : 'R'; h.playAction(side === 'R' ? 'bolt_r' : 'bolt_l', yawOf(ev.dx, ev.dz) ?? aim); this.tag(sim, ev.x, ev.z, [0, 1], side); }
        // Finger Beam: reuses the approved one-arm Spirit Bolt thrust (right hand) as a quick point toward the needle's line
        else if (ev.w === 'finger' && h.allows('bolt_r')) h.playAction('bolt_r', yawOf(ev.dx, ev.dz) ?? aim);
        else if (ev.w === 'breaker' && h.allows('breaker')) { h.playAction('breaker', yawOf(ev.dx, ev.dz) ?? aim); this.tag(sim, ev.x, ev.z, [5], 'R'); }
        else if (ev.w === 'bore' && h.allows('bore')) { h.playAction('bore', yawOf(ev.dx, ev.dz) ?? aim); this.tag(sim, ev.x, ev.z, [6], null); }
        break;
      case 'disc': if (h.allows('disc')) { h.playAction('disc', yawOf(ev.dx, ev.dz) ?? aim); this.tag(sim, p.x, p.z, [2], 'R'); } break;
      case 'skyfall': if (h.allows('skyfall')) { h.playAction('skyfall', aim); this.skyCap = 0.14; } break;
      case 'mortar': h.playAction('mortar_push', Math.atan2(ev.dx, ev.dz)); break;
      case 'zenithThrow': {
        let best: Projectile | null = null;
        for (const pr of sim.projectiles) if (pr.alive && !pr.hostile && pr.kind === 4 && (!best || pr.life > best.life)) best = pr;
        h.playAction('zenith_throw', best ? Math.atan2(best.vx, best.vz) : aim);
        break;
      }
      case 'arc': if (ev.pts.length >= 6) h.playAction('arc', Math.atan2(ev.pts[3] - p.x, ev.pts[5] - p.z)); break;
      case 'glare': h.playAction('glare'); break;
      case 'lure': h.playAction('lure'); break;
      case 'transform': h.playAction('powerup'); break;
      // Overdrive is retired from the game (2026-11-02): its act_overdrive clip stays in the GLB but is not hooked up
    }
  }

  /** per frame, before GltfHero.update: charge holds follow the sim state; muzzle/launch bookkeeping */
  sync(dt: number, sim: Sim): void {
    const h = this.hero();
    if (!h) { this.muz.clear(); this.sky.clear(); return; }
    const p = sim.player;
    h.hold('mortar_charge', sim.mortar.charging, Math.atan2(p.aimX, p.aimZ));
    h.hold('zenith_hold', sim.zenith.charging);
    h.hold('rift', sim.rift.active, Math.atan2(Math.cos(sim.rift.a), Math.sin(sim.rift.a))); // rift angle is atan2(z, x)
    for (const [pr, m] of this.muz) { m.age += dt; if (!pr.alive || m.age >= m.T) this.muz.delete(pr); }
    this.skyCap = Math.max(0, this.skyCap - dt);
    for (const f of this.sky.keys()) if (!sim.falls.includes(f as Sim['falls'][number])) this.sky.delete(f);
  }

  /** drawn position of a player shot: eased from the firing hand onto the sim path while young (else the sim position) */
  /** Where to draw a projectile, in SIM space (y above the local ground; the caller adds the hill height). yOff shifts the
   *  drawn flight height (Spirit Bolt flies at hand height, owner 2026-11-02); hill = ground hill height at (x, z) so the
   *  hand position (world space) is brought back to sim space — it used to get the hill added twice */
  drawPos(pr: Projectile, yOff = 0, hill?: (x: number, z: number) => number): { x: number; y: number; z: number } {
    const o = this._o; o.x = pr.x; o.y = pr.y + yOff; o.z = pr.z;
    const m = this.muz.get(pr); if (!m) return o;
    const h = this.hero(); if (!h) return o;
    const u = Math.min(1, m.age / m.T), e = u * u;
    h.handWorld(m.side, this._h);
    const hy = this._h.y - (hill ? hill(this._h.x, this._h.z) : 0);
    o.x = this._h.x + (pr.x - this._h.x) * e; o.y = hy + (pr.y + yOff - hy) * e; o.z = this._h.z + (pr.z - this._h.z) * e;
    return o;
  }

  /** Skyfall launch point for a fall: follows the flinging hand for the first ~0.14 s, then stays put (null = default) */
  skyAnchor(f: Sim['falls'][number]): THREE.Vector3 | null {
    const h = this.hero(); if (!h) return null;
    let a = this.sky.get(f);
    if (!a) { if (this.skyCap <= 0 && f.t < f.t0 - 0.2) return null; a = new THREE.Vector3(); this.sky.set(f, a); h.handWorld(f.k % 2 ? 'L' : 'R', a); }
    else if (this.skyCap > 0) h.handWorld(f.k % 2 ? 'L' : 'R', a);
    return a;
  }

  /** world yaw the standing hero should square up to (null = keep the normal facing) */
  facing(): number | null { return this.hero()?.actionFacing() ?? null; }

  /** tag fresh player shots of `kinds` near (x, z) with the hand that throws them (null = nearest hand) */
  private tag(sim: Sim, x: number, z: number, kinds: number[], side: Side | null): void {
    const h = this.hero(); if (!h) return;
    let hl: THREE.Vector3 | null = null, hr: THREE.Vector3 | null = null;
    for (const pr of sim.projectiles) {
      if (!pr.alive || pr.hostile || !kinds.includes(pr.kind) || this.muz.has(pr) || (pr.kind === 2 && pr.ret)) continue;
      if ((pr.x - x) ** 2 + (pr.z - z) ** 2 > 9) continue; // fresh shots only (a few ticks of flight at most)
      let s = side;
      if (!s) {
        hl ??= h.handWorld('L', this._h).clone(); hr ??= h.handWorld('R', this._h2).clone();
        s = (pr.x - hl.x) ** 2 + (pr.z - hl.z) ** 2 < (pr.x - hr.x) ** 2 + (pr.z - hr.z) ** 2 ? 'L' : 'R';
      }
      this.muz.set(pr, { age: 0, side: s, T: MUZZLE_T[pr.kind] ?? 0.12 });
      this.stats.tagged++;
    }
  }
}
